import { useState } from 'react';
import { Calculator, Plus, Trash2 } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import { formatNumber, MAX_MANUAL_ROWS } from '../lib/pricing';

export default function PricingTable() {
  const {
    pricingCalculations,
    handleListPriceChange,
    handleUpdatePriceRow,
    handleAddManualPriceRow,
    handleRemovePriceRow,
    readOnlyState,
    proposalType,
    units,
    activeUnitId,
  } = useAppContext();

  const [campaignDraft, setCampaignDraft] = useState<Record<string, string>>({});
  const activeUnitLabel = proposalType === 'bulk' ? units.find(u => u.id === activeUnitId)?.label : undefined;

  // Converts a fraction (0.442) to a trimmed Turkish-style percent string ("44,2").
  const formatCampaignPercent = (fraction: number): string => {
    const trimmed = parseFloat((fraction * 100).toFixed(4)).toString();
    return trimmed.replace('.', ',');
  };

  // Keeps only digits and a single decimal separator (comma or dot) while typing.
  const sanitizeDecimalInput = (raw: string): string => {
    let cleaned = raw.replace(/[^0-9.,]/g, '');
    const firstSepIndex = cleaned.search(/[.,]/);
    if (firstSepIndex !== -1) {
      cleaned = cleaned.slice(0, firstSepIndex + 1) + cleaned.slice(firstSepIndex + 1).replace(/[.,]/g, '');
    }
    return cleaned;
  };

  const handleCampaignDiscountChange = (rowKey: string, raw: string) => {
    const cleaned = sanitizeDecimalInput(raw);
    setCampaignDraft(prev => ({ ...prev, [rowKey]: cleaned }));

    if (cleaned === '') {
      handleUpdatePriceRow(rowKey, { campaignDiscount: '' });
      return;
    }
    const normalized = cleaned.replace(',', '.');
    const parsed = parseFloat(normalized);
    if (!isNaN(parsed)) {
      handleUpdatePriceRow(rowKey, { campaignDiscount: parsed / 100 });
    }
  };

  const handleCampaignDiscountBlur = (rowKey: string) => {
    setCampaignDraft(prev => {
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
  };

  const manualRowCount = pricingCalculations.rows.filter(r => r.isManual).length;

  return (
    <div className="bg-white p-6 rounded-xl border border-stone-200/80 shadow-card space-y-4 h-full flex flex-col">
      <div className="flex items-center justify-between border-b border-stone-100 pb-3">
        <div className="flex items-center space-x-2 text-stone-800 font-semibold">
          <Calculator className="w-4 h-4 text-brand-600" />
          <span className="text-xs uppercase tracking-wider font-semibold text-stone-700">Teklif Fiyatlandırma Tablosu</span>
        </div>
        {activeUnitLabel && (
          <span className="text-[10px] font-semibold uppercase tracking-wider text-brand-600 bg-brand-50/80 border border-brand-200/60 px-2.5 py-0.5 rounded-full">
            {activeUnitLabel}
          </span>
        )}
      </div>

      <div className="overflow-x-auto border border-stone-200/70 rounded-lg">
        <table className="w-full text-left border-collapse text-xs">
          <thead>
            <tr className="bg-stone-50/80 border-b border-stone-200/60 text-stone-500 text-[10.5px] font-medium tracking-wide">
              <th className="py-2.5 px-3">Kategori</th>
              <th className="py-2.5 px-3 w-40 text-center">Tavsiye Liste Fiyatı (TL)</th>
              <th className="py-2.5 px-3 w-28 text-center">Kampanya İnd.</th>
              <th className="py-2.5 px-3 w-28 text-center">Ek İndirim</th>
              <th className="py-2.5 px-3 w-24 text-center">KDV %</th>
              <th className="py-2.5 px-3 w-36 text-center">KDV Dahil Tutar (TL)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {pricingCalculations.rows.map(row => {
              const isEmpty = row.listPrice === '';
              return (
                <tr key={row.key} className="hover:bg-stone-50/50 transition-all">
                  <td className="py-2.5 px-3 font-medium text-stone-800">
                    {row.isManual ? (
                      <div className="flex items-center space-x-1.5">
                        <input
                          type="text"
                          maxLength={60}
                          placeholder="Kalem adı (örn. Ankastre Set)"
                          disabled={readOnlyState.isReadOnly}
                          value={row.categoryName}
                          onChange={e => handleUpdatePriceRow(row.key, { categoryName: e.target.value })}
                          className="w-full min-w-[9rem] px-2 py-1 rounded-md border border-stone-200 bg-white disabled:bg-stone-50 disabled:cursor-not-allowed focus:outline-none focus:ring-1 focus:ring-brand-500/30 focus:border-brand-500 transition-all"
                        />
                        {!readOnlyState.isReadOnly && (
                          <button
                            type="button"
                            onClick={() => handleRemovePriceRow(row.key)}
                            className="text-stone-400 hover:text-red-600 p-1 rounded hover:bg-red-50 transition-colors shrink-0"
                            title="Bu satırı kaldır"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    ) : row.displayName}
                  </td>
                  <td className="py-2 px-3 text-center">
                    <input
                      type="text"
                      inputMode="numeric"
                      disabled={readOnlyState.isReadOnly}
                      value={row.listPrice === '' ? '' : row.listPrice.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                      onChange={e => handleListPriceChange(row.key, e.target.value)}
                      className="w-28 text-right px-2 py-1 rounded-md border border-stone-200 bg-white disabled:bg-stone-50 disabled:cursor-not-allowed font-medium tabular-nums focus:outline-none focus:ring-1 focus:ring-brand-500/30 focus:border-brand-500 transition-all mx-auto block"
                    />
                  </td>
                  <td className="py-2 px-2 text-center">
                    <div className="flex items-center justify-center space-x-1">
                      <input
                        type="text"
                        inputMode="decimal"
                        disabled={readOnlyState.isReadOnly}
                        value={campaignDraft[row.key] ?? (row.campaignDiscount === '' ? '' : formatCampaignPercent(row.campaignDiscount))}
                        onChange={e => handleCampaignDiscountChange(row.key, e.target.value)}
                        onBlur={() => handleCampaignDiscountBlur(row.key)}
                        className="w-14 text-right px-1.5 py-1 rounded-md border border-stone-200 focus:outline-none focus:ring-1 focus:ring-brand-500/30 focus:border-brand-500 bg-white disabled:bg-stone-50 disabled:cursor-not-allowed tabular-nums transition-all"
                      />
                      <span className="text-[10px] text-stone-400 font-medium">%</span>
                    </div>
                  </td>
                  <td className="py-2 px-2 text-center">
                    <div className="flex items-center justify-center space-x-1">
                      <input
                        type="number"
                        disabled={readOnlyState.isReadOnly}
                        value={row.extraDiscount === '' ? '' : Math.round(row.extraDiscount * 100)}
                        min={0}
                        max={100}
                        onChange={e => handleUpdatePriceRow(row.key, { extraDiscount: e.target.value === '' ? '' : ((parseFloat(e.target.value) || 0) / 100) })}
                        className="w-12 text-right px-1.5 py-1 rounded-md border border-stone-200 focus:outline-none focus:ring-1 focus:ring-brand-500/30 focus:border-brand-500 bg-white disabled:bg-stone-50 disabled:cursor-not-allowed tabular-nums transition-all"
                      />
                      <span className="text-[10px] text-stone-400 font-medium">%</span>
                    </div>
                  </td>
                  <td className="py-2 px-2 text-center">
                    <select
                      disabled={readOnlyState.isReadOnly}
                      value={row.vatRate}
                      onChange={e => handleUpdatePriceRow(row.key, { vatRate: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                      className="px-2 py-1 rounded-md border border-stone-200 bg-white disabled:bg-stone-50 disabled:cursor-not-allowed focus:outline-none focus:ring-1 focus:ring-brand-500/30 focus:border-brand-500 text-xs transition-all mx-auto"
                    >
                      <option value="">Seçiniz</option>
                      <option value={0.10}>%10</option>
                      <option value={0.20}>%20</option>
                      <option value={0.00}>%0</option>
                    </select>
                  </td>
                  <td className="py-2 px-3 text-center tabular-nums">
                    {isEmpty ? (
                      <div className="flex flex-col items-center leading-tight">
                        <span className="text-stone-300 text-sm">—</span>
                        <span className="text-stone-400 text-[9px] font-normal">henüz girilmedi</span>
                      </div>
                    ) : (
                      <span className="font-semibold text-stone-900">{formatNumber(row.incVatPrice)}</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {!readOnlyState.isReadOnly && (
        <button
          type="button"
          onClick={handleAddManualPriceRow}
          disabled={manualRowCount >= MAX_MANUAL_ROWS}
          className="w-full py-2 bg-brand-50/80 hover:bg-brand-100 text-brand-700 border border-brand-200/60 rounded-lg text-xs font-semibold flex items-center justify-center space-x-1.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-3.5 h-3.5" />
          <span>{manualRowCount >= MAX_MANUAL_ROWS ? `En fazla ${MAX_MANUAL_ROWS} manuel satır eklenebilir` : 'Manuel Satır Ekle'}</span>
        </button>
      )}
    </div>
  );
}
